/**
 * Utilities — shared helper functions used across the theme.
 * Matches Horizon's export API for compatibility.
 */

/** requestIdleCallback with fallback */
export const requestIdleCallback =
  typeof window.requestIdleCallback === 'function' ? window.requestIdleCallback : setTimeout;

/** Yield to main thread for long task optimization */
export const yieldToMainThread = () => {
  if ('yield' in scheduler) {
    return /** @type {any} */ (scheduler).yield();
  }
  return new Promise((resolve) => {
    requestAnimationFrame(() => setTimeout(resolve, 0));
  });
};

/**
 * Create a fetch configuration object
 * @param {string} [type='json']
 * @param {{ body?: string | FormData, headers?: Record<string, string> }} [config={}]
 * @returns {RequestInit}
 */
export function fetchConfig(type = 'json', config = {}) {
  const headers = {
    'Content-Type': 'application/json',
    Accept: `application/${type}`,
    ...config.headers,
  };

  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest';
    delete headers['Content-Type'];
  }

  return {
    method: 'POST',
    headers,
    body: config.body,
  };
}

/**
 * Debounce a function
 * @template {(...args: any[]) => any} T
 * @param {T} fn
 * @param {number} wait
 * @returns {T & { cancel(): void }}
 */
export function debounce(fn, wait) {
  let timeout;
  function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  }
  debounced.cancel = () => clearTimeout(timeout);
  return /** @type {any} */ (debounced);
}

/**
 * Throttle a function
 * @template {(...args: any[]) => any} T
 * @param {T} fn
 * @param {number} delay
 * @returns {T & { cancel(): void }}
 */
export function throttle(fn, delay) {
  let lastCall = 0;
  function throttled(...args) {
    const now = performance.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn.apply(this, args);
    }
  }
  throttled.cancel = () => { lastCall = performance.now(); };
  return /** @type {any} */ (throttled);
}

/** @type {MediaQueryList} */
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/** Check if user prefers reduced motion */
export function prefersReducedMotion() {
  return reducedMotion.matches;
}

/** Media query for large screens (750px+) */
export const mediaQueryLarge = matchMedia('(min-width: 750px)');

/** @returns {boolean} */
export function isMobileBreakpoint() {
  return !mediaQueryLarge.matches;
}

/** @returns {boolean} */
export function isDesktopBreakpoint() {
  return mediaQueryLarge.matches;
}

/** @returns {boolean} */
export function isTouchDevice() {
  return 'ontouchstart' in window && navigator.maxTouchPoints > 0;
}

/**
 * Clamp a number between min and max
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

/**
 * Normalize a string (remove diacritics, lowercase)
 * @param {string} str
 * @returns {string}
 */
export function normalizeString(str) {
  return str.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

/**
 * Run callback when document is fully loaded
 * @param {() => void} callback
 */
export function onDocumentLoaded(callback) {
  if (document.readyState === 'complete') {
    callback();
  } else {
    window.addEventListener('load', callback);
  }
}

/**
 * Run callback when DOM is ready (before resources loaded)
 * @param {() => void} callback
 */
export function onDocumentReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback);
  } else {
    callback();
  }
}

/**
 * Check if click was outside element
 * @param {MouseEvent} event
 * @param {Element} element
 * @returns {boolean}
 */
export function isClickedOutside(event, element) {
  if (!(event.target instanceof Element)) {
    return !isPointWithinElement(event.clientX, event.clientY, element);
  }
  // When clicking a <dialog> backdrop, event.target === the dialog itself.
  // element.contains(element) is true, so we'd wrongly think it's inside.
  // Fall back to coordinate check when target IS the element.
  if (event.target === element) {
    return !isPointWithinElement(event.clientX, event.clientY, element);
  }
  return !element.contains(event.target);
}

/**
 * Check if point is within element bounds
 * @param {number} x
 * @param {number} y
 * @param {Element} element
 * @returns {boolean}
 */
export function isPointWithinElement(x, y, element) {
  const { left, right, top, bottom } = element.getBoundingClientRect();
  return x >= left && x <= right && y >= top && y <= bottom;
}

/** @param {Event} event */
export function preventDefault(event) {
  event.preventDefault();
}

/**
 * Wait for all animations to finish
 * @param {Element | Element[]} elements
 * @param {() => void} [callback]
 * @returns {Promise<void>}
 */
export function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const animations = Array.isArray(elements)
    ? elements.flatMap((el) => el.getAnimations(options))
    : elements.getAnimations(options);

  const promises = animations
    .filter((a) => a.timeline instanceof DocumentTimeline)
    .map((a) => a.finished);

  return Promise.allSettled(promises).then(callback);
}

/** Remove will-change after animation ends */
export function removeWillChangeOnAnimationEnd(event) {
  const target = event.target;
  if (target instanceof HTMLElement) {
    target.style.setProperty('will-change', 'unset');
    target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
  }
}

/* ════════════════════════════════════════════
   Header utilities
   ════════════════════════════════════════════ */

/**
 * Set header menu style based on overflow and device
 */
export function setHeaderMenuStyle() {
  const headerComponent = document.querySelector('#header-component');
  if (!headerComponent) return;

  const touchDevice = isTouchDevice();
  const overflowList = headerComponent.querySelector('overflow-list');
  const hasReachedMinimum = overflowList?.hasAttribute('minimum-reached');

  /** @type {HTMLElement} */ (headerComponent).dataset.menuStyle =
    touchDevice || hasReachedMinimum ? 'drawer' : 'menu';
}

/**
 * Calculate header group height for CSS custom property
 * @param {HTMLElement} header
 * @returns {number}
 */
export function calculateHeaderGroupHeight(header) {
  const headerGroup = document.querySelector('#header-group');
  if (!headerGroup) return 0;

  let height = 0;
  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const el = children[i];
    if (el === header || !(el instanceof HTMLElement)) continue;
    height += el.offsetHeight;
  }

  if (header?.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
    height += header.offsetHeight;
  }

  return height;
}

/**
 * Change the meta theme-color
 * @param {string} color
 */
export function changeMetaThemeColor(color) {
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', color);
}